import cn from 'classnames'
import React, { memo, ReactElement, ReactNode, useMemo } from 'react'
import { motion } from 'framer-motion'
import { IconType } from 'core/components/icons/types'
import Icon from 'core/components/icons/Icon'

interface TagProps {
  children: ReactNode
  prefix?: `${IconType}` | ReactElement
  suffix?: `${IconType}` | ReactElement
  variant?: 'default' | 'primary' | 'danger'
  className?: string
  value: string | number
  disabled?: boolean
}

const variantStyles = {
  default: 'bg-dark-8 text-dark',
  primary: 'bg-primary-1 text-primary',
  danger: 'bg-danger-1 text-danger'
}
const motionVariants = {
  show: { scale: 1, opacity: 1 },
  hide: { scale: 0.7, opacity: 0 }
}

const Tag = memo(
  ({
    children,
    variant = 'default',
    className,
    value,
    disabled,
    ...props
  }: TagProps) => {
    const prefix = useMemo(() => {
      if (props.prefix && (props.prefix as string) in IconType) {
        return (
          <Icon name={props.prefix as IconType} size={12} className="mr-2" />
        )
      }

      return props?.prefix ? (
        <span className="mr-2">{props?.prefix}</span>
      ) : null
    }, [props.prefix])

    const suffix = useMemo(() => {
      if (props.suffix && (props.suffix as string) in IconType) {
        return (
          <Icon name={props.suffix as IconType} size={12} className="ml-2" />
        )
      }

      return props?.suffix ? (
        <span className="ml-2">{props?.suffix}</span>
      ) : null
    }, [props.suffix])

    return (
      <motion.button
        type="button"
        value={value}
        variants={motionVariants}
        initial="hide"
        animate="show"
        exit="hide"
        disabled={disabled}
        className={cn(
          'inline-flex items-center rounded-full !leading-5 text-sm px-4 py-1 font-medium disabled:pointer-events-none',
          variantStyles[variant],
          className
        )}
      >
        {prefix}
        <span className="inline-block grow overflow-x-hidden text-ellipsis">
          {children}
        </span>
        {suffix}
      </motion.button>
    )
  }
)

export default Tag
